import {
  AppLayout,
  Box,
  ColumnLayout,
  Container,
  Flashbar,
  FlashbarProps,
  FormField,
  Link,
  Select,
  SelectProps,
  SpaceBetween
} from '@amzn/awsui-components-core';
import React, { useEffect, useState } from 'react';
import { fetchGraphQLRequest } from 'src/api/graphql-api';

import { EmptyState } from 'src/components/generic-components/empty-state';
import LastRefreshedAt from 'src/components/generic-components/LastRefreshedAt';
import { appSyncOperationName, TICKETS_FOR_OE_ERROR_URL } from 'src/constants/AppConstants';
import * as queries from 'src/graphql/queries';
import { logger } from 'src/logger';
import { recordApiRequest } from 'src/services/aws/CloudWatchRumService';
import { buildBreadcrumbChain } from 'src/utilities/RouteConfig';
import { getCurrentTime, getTimeDifference } from 'src/utilities/DateTimeUtilities';
import { OESideNavigation } from '../fintech-ops-dashboard/OESideNavigation';
import { filtersLabel } from '../FinTechOpsCommonElements';
import { FinTechOpsMessages } from '../FinTechOpsConstants';
import { stringArrayToOptionsObj } from '../FinTechOpsDataProcessingFunctions';
import { EmptyStateMsg } from './MappingConstants';
import { MappingConfig } from './MappingInterface';
import { MappingTable } from './MappingTable';
import { Titles } from 'src/components/das-finsuite/das-finsuite-constants';

export const MappingMainPage: React.FC = () => {
  const [flashbarItems, setFlashbarItems] = useState<FlashbarProps.MessageDefinition[]>([]);

  const [lastRefreshed, setLastRefreshed] = useState('');
  const [mappingTableSelected, setMappingTableSelected] = useState<SelectProps.Option>({ label: 'Select Mapping Tables', value: '' });

  const [mappingConfigSelectData, setMappingConfigSelectData] = useState([]);

  const [mappingConfigData, setMappingConfigData] = useState<MappingConfig[]>([]);

  const [selectedConfig, setSelectedConfig] = useState('');

  const updateLastRefreshed = (val?: string) => {
    if (val) {
      setLastRefreshed(val);
    }
  };

  //Function to update the mapping selection
  const updateMappingTableSelected = (detail: SelectProps.ChangeDetail) => {
    setMappingTableSelected(detail.selectedOption);
    setLastRefreshed('');
  };

  //Setting the selectedConfig value based on change of value of drop down
  useEffect(() => {
    const selectedValue = mappingConfigData.find((item) => item.mapping_name === mappingTableSelected.value);
    setSelectedConfig(selectedValue?.mapping_key || '');
  }, [mappingTableSelected.value]);

  //one time retrieval of mapping config data
  useEffect(() => {
    getMappingConfig();
  }, []);

  //Function to retrieve the mapping config for the drop down
  const getMappingConfig = async () => {
    try {
      const start = getCurrentTime();
      const mappingConfigResponse: any = await fetchGraphQLRequest({
        query: queries.listMappingConfig,
        variables: {},
        requestName: 'listMappingConfig',
        requestType: appSyncOperationName.QUERY
      });

      const elapsed = getTimeDifference(start);
      logger.info(`Fetched Mapping Config Data in ${elapsed} ms`);
      recordApiRequest('listMappingConfig', elapsed);
      setMappingConfigSelectData(
        mappingConfigResponse.listMappingConfig.mapping_config.data.map((item: { mapping_name: string }) => item.mapping_name)
      );
      setMappingConfigData(mappingConfigResponse.listMappingConfig.mapping_config.data);
    } catch (error: any) {
      logger.error('Unable to load Mapping Config data', error);
      displayNotification(`error`, FinTechOpsMessages.APIError, TICKETS_FOR_OE_ERROR_URL);
    }
  };

  const displayNotification = (status: FlashbarProps.Type, message: string | React.ReactNode, linkMessage?: string) => {
    function addTicketLink() {
      if (linkMessage) {
        return (
          <Link variant="primary" target="_blank" href={linkMessage} color="inverted" fontSize="body-m">
            submit a ticket
          </Link>
        );
      }
    }

    setFlashbarItems([
      {
        type: status,
        content: (
          <>
            {message} {addTicketLink()}
          </>
        ),
        dismissible: true,
        dismissLabel: 'Dismiss message',
        onDismiss: () => setFlashbarItems([]),
        id: 'message_1'
      }
    ]);
  };

  //Function will dismiss notification
  const dismissNotification = () => {
    setFlashbarItems([]);
  };

  return (
    <AppLayout
      contentType="cards"
      navigation={<OESideNavigation />}
      toolsHide={true}
      notifications={<Flashbar items={flashbarItems} />}
      maxContentWidth={Number.MAX_VALUE}
      content={
        <div className="app-layout-body">
          <Box>
            <Container
              fitHeight
              header={
                <SpaceBetween size="m" direction="horizontal" alignItems="center">
                  <Box variant="h2">{Titles.TITLE_OP_MAPPING}</Box>
                  <LastRefreshedAt lastRefreshedDateTime={lastRefreshed} />
                </SpaceBetween>
              }
            >
              <SpaceBetween size="m" direction="vertical">
                <ColumnLayout columns={3}>
                  <FormField label={filtersLabel('Mapping Config')}>
                    <Select
                      placeholder={'Select Mapping'}
                      onChange={({ detail }) => updateMappingTableSelected(detail)}
                      options={stringArrayToOptionsObj(mappingConfigSelectData)}
                      selectedOption={mappingTableSelected}
                      expandToViewport
                      loadingText="Loading Mapping Options"
                      errorText="Unable to load Mapping Options"
                    />
                  </FormField>
                </ColumnLayout>
              </SpaceBetween>
            </Container>
          </Box>

          <Box padding={{ top: 's' }}>
            {selectedConfig ? (
              <MappingTable
                lastRefreshedVal={updateLastRefreshed}
                mappingConfig={selectedConfig}
                mappingName={mappingTableSelected.value}
                notifyUser={(status: FlashbarProps.Type, message: string | React.ReactNode, linkMessage?: string, dismiss?: boolean) => {
                  displayNotification(status, message, linkMessage);
                }}
                resetNotification={dismissNotification}
              />
            ) : (
              <EmptyState title={EmptyStateMsg.Title} description={EmptyStateMsg.Description} verticalCenter={true} />
            )}{' '}
          </Box>
        </div>
      }
    />
  );
};
